import {
  type BrokerStatus,
  brokerWillPlace,
  isDesk,
  type Viewer,
} from '@alphadesk/web/contract.ts';
import { brokerLoginLink, PAGES } from '@alphadesk/web/links.ts';
import clsx from 'clsx';
import { Check, ExternalLink, Landmark } from 'lucide-react';
import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCommit } from '../lib/act.ts';
import { type BrokerState, useBroker } from '../lib/broker.ts';
import { istStamp } from '../lib/format.ts';
import { forgetQuery, useLinkClick } from '../lib/router.ts';
import { PageSection, Row, Rows, StatusDot, type Tone } from './pages/Sections.tsx';
import { Button, buttonClass } from './primitives/button.tsx';
import { Checkbox, field, linkClass, RefreshButton, Switch } from './primitives/controls.tsx';
import { Card, Notice, SECTION } from './primitives/shell.tsx';
import { Confirm } from './primitives/ticket.tsx';
import { useToast } from './Toast.tsx';

/** One fetch for the header, the account and the tickets, so they never disagree about placing orders. */
const NOBODY: BrokerState = { status: null, checking: false, refresh: () => {} };
const BrokerContext = createContext<BrokerState>(NOBODY);

const BROKER_HREF = `${PAGES.account}#broker`;

export function BrokerProvider({ viewer, children }: { viewer: Viewer; children: ReactNode }) {
  const desk = isDesk(viewer);
  const { status, checking, refresh } = useBroker(!desk);
  const toast = useToast();
  // The param is stripped so a reload does not repeat it.
  useEffect(() => {
    const connected = new URLSearchParams(window.location.search).get('broker') === 'connected';
    forgetQuery(['broker', 'panel']);
    if (connected && !desk) toast.done('Your broker is connected for today.');
  }, [desk, toast]);
  const value = useMemo(() => ({ status, checking, refresh }), [status, checking, refresh]);
  return <BrokerContext.Provider value={value}>{children}</BrokerContext.Provider>;
}

export const useBrokerStatus = (): BrokerStatus | null => useContext(BrokerContext).status;

interface BrokerLook {
  tone: Tone;
  label: string;
  /** The state in a few words, where the line will not fit. */
  short: string;
  line: string;
  login: 'primary' | 'quiet' | null;
}

function brokerLook(status: BrokerStatus): BrokerLook {
  if (!status.linked && status.asked) {
    return {
      tone: status.asked.failed ? 'warn' : 'off',
      label: 'Broker',
      short: 'Being set up',
      line: 'The desk is setting up your Zerodha.',
      login: null,
    };
  }
  if (!status.linked) {
    return {
      tone: 'off',
      label: 'No broker',
      short: 'Not linked',
      line: 'Nothing of yours reaches a broker.',
      login: null,
    };
  }
  const until = status.expiresAt ? istStamp(status.expiresAt) : null;
  switch (status.session) {
    case 'ok':
      return {
        tone: 'good',
        label: status.kiteUserId ?? 'Broker',
        short: 'Logged in',
        line: until
          ? `Logged in — your broker confirms it · good until ${until}`
          : 'Logged in — your broker confirms it.',
        login: 'quiet',
      };
    case 'ended':
      return {
        tone: 'warn',
        label: 'Log in',
        short: 'Session ended',
        line:
          'Your broker has ended today’s session — logging into its website or app does that. ' +
          'Start a fresh one.',
        login: 'primary',
      };
    case 'unreachable':
      return {
        tone: 'warn',
        label: 'Broker?',
        short: 'Not answering',
        line: until
          ? `Logged in until ${until} by my record, but your broker isn’t answering right now, ` +
            'so I couldn’t confirm it.'
          : 'Logged in by my record, but your broker isn’t answering right now, so I couldn’t confirm it.',
        login: 'quiet',
      };
    case 'logged-out':
      return {
        tone: 'warn',
        label: 'Log in',
        short: 'Logged out',
        line: 'Not logged in to your broker today. A session lasts until 6 AM and no longer.',
        login: 'primary',
      };
  }
}

export function placingBlock(status: BrokerStatus): { line: string; login: boolean } | null {
  if (brokerWillPlace(status)) return null;
  if (!status.placing) {
    return {
      line: 'Placing your orders is off — your account page turns it on.',
      login: false,
    };
  }
  switch (status.refusal) {
    case 'logged-out':
      return {
        line: 'Not logged in to your broker today — a session lasts until 6 AM.',
        login: true,
      };
    case 'trading-disabled':
      return { line: 'This deployment can’t write to your broker right now.', login: false };
    case 'not-linked':
      return { line: 'No account linked — link one on your account page.', login: false };
    case 'unavailable':
    case 'no-member':
    case 'not-opted-in':
    case null:
      return { line: 'Your broker can’t be reached for a new order right now.', login: false };
  }
}

function loginFrom(path: string, search: string): string {
  const query = new URLSearchParams(search);
  query.delete('broker');
  query.delete('panel');
  const rest = query.toString();
  return brokerLoginLink(`${path}${rest ? `?${rest}` : ''}`);
}

const loginHere = (): string => loginFrom(window.location.pathname, window.location.search);

/** A ticket rewrites the query without a render, so the address is read again as the link is followed. */
const followHere = (event: MouseEvent<HTMLAnchorElement>): void => {
  event.currentTarget.href = loginHere();
};

/** New tab: it sits inside half-filled tickets, which a login must not lose. */
export function BrokerLoginLink() {
  return (
    <a
      href={loginHere()}
      onClick={followHere}
      target="_blank"
      rel="noopener"
      className={linkClass('quiet', 'inline-flex items-center gap-1')}
    >
      <ExternalLink aria-hidden className="size-3" />
      Log in
    </a>
  );
}

export function BrokerBox({
  status,
  place,
  onChange,
  label,
  absent,
  placeLine,
  keepLine,
}: {
  status: BrokerStatus | null;
  place: boolean;
  onChange: (next: boolean) => void;
  label: string;
  absent: string;
  placeLine?: string;
  keepLine: string;
}) {
  if (!status?.available || !status.linked) {
    return <p className="text-xs text-ink-faint">{absent}</p>;
  }

  const blocked = placingBlock(status);
  return (
    <Checkbox
      checked={place}
      disabled={blocked !== null}
      onChange={onChange}
      label={
        <>
          {label}
          {status.kiteUserId && (
            <span className="ph-no-capture ml-1 text-ink-faint">{status.kiteUserId}</span>
          )}
        </>
      }
    >
      {blocked ? (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-warn">
          {blocked.line}
          {blocked.login && <BrokerLoginLink />}
        </span>
      ) : (
        (place ? placeLine : keepLine) && (
          <span className="text-xs text-ink-faint">{place ? placeLine : keepLine}</span>
        )
      )}
    </Checkbox>
  );
}

const sessionGone = (status: BrokerStatus | null): boolean =>
  status?.available === true &&
  status.linked &&
  (status.session === 'logged-out' || status.session === 'ended');

const loginNeeded = (status: BrokerStatus | null): boolean =>
  sessionGone(status) && status?.placing === true;

/** Same tab: Kite's redirect returns to this page, connected. */
function BrokerLogin({
  tone = 'primary',
  children = 'Log in to your broker',
}: {
  tone?: 'primary' | 'quiet';
  children?: ReactNode;
}) {
  return (
    <a
      href={loginHere()}
      onClick={followHere}
      title="Takes this tab to your broker’s login, and back here connected"
      className={clsx(buttonClass(tone), 'shrink-0 justify-center')}
    >
      <ExternalLink aria-hidden className="size-3.5" />
      {children}
    </a>
  );
}

/** Deliberately not dismissible: it clears itself once the session is back. */
export function BrokerBanner() {
  const { status } = useContext(BrokerContext);
  if (!loginNeeded(status) || !status) return null;

  return (
    <div role="status" className="border-b border-warn/20 bg-warn/10 py-2">
      <div className="rail flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <p className="min-w-0 flex-1 text-xs leading-relaxed text-warn">
          {status.session === 'ended'
            ? 'Your broker ended today’s session — nothing of yours can be placed until you log in again.'
            : 'Not logged in to your broker today — nothing of yours can be placed until you log in.'}
        </p>
        <BrokerLogin />
      </div>
    </div>
  );
}

export function BrokerPill({ viewer }: { viewer: Viewer }) {
  const { status } = useContext(BrokerContext);
  const onClick = useLinkClick();
  if (isDesk(viewer) || !status?.available) return null;
  const look = brokerLook(status);
  return (
    <a
      href={BROKER_HREF}
      onClick={onClick}
      aria-label={`Your broker — ${look.label}`}
      title={look.line}
      className="hidden items-center gap-1.5 rounded-control border border-line-soft px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:border-line hover:text-ink sm:inline-flex"
    >
      <StatusDot tone={look.tone} />
      {look.label}
    </a>
  );
}

export function BrokerSection({ viewer }: { viewer: Viewer }) {
  const { status, checking, refresh } = useContext(BrokerContext);
  if (isDesk(viewer) || !status?.available) return null;
  const look = brokerLook(status);
  return (
    <PageSection
      id="broker"
      title="Broker"
      blurb="Your own Zerodha account. The desk reads it, and trades it only while you let it."
      status={{ tone: look.tone, label: look.short }}
    >
      <YourBroker status={status} look={look} checking={checking} onChanged={refresh} />
    </PageSection>
  );
}

export function LinkBroker({ title, gains }: { title: string; gains: readonly string[] }) {
  const onClick = useLinkClick();
  const { status } = useContext(BrokerContext);
  if (status === null) return null;
  if (!status.available) {
    return <Notice tone="quiet">This desk reaches no broker right now.</Notice>;
  }
  return (
    <Card className="max-w-2xl items-start">
      <span className="flex size-9 items-center justify-center rounded-full bg-surface-raised text-ink">
        <Landmark aria-hidden className="size-4.5" />
      </span>
      <h2 className={SECTION}>{title}</h2>
      <ul className="flex flex-col gap-1.5">
        {gains.map((gain) => (
          <li key={gain} className="flex gap-2 text-sm leading-relaxed text-ink-muted">
            <Check aria-hidden className="mt-1 size-3.5 shrink-0 text-ink" />
            {gain}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
        <a href={BROKER_HREF} onClick={onClick} className={buttonClass('primary')}>
          <Landmark aria-hidden className="size-3.5" />
          Link your Zerodha
        </a>
        <span className="text-xs text-ink-faint">
          Just your Zerodha client ID. The desk never holds your money.
        </span>
      </div>
    </Card>
  );
}

function AskForm({ onAsked }: { onAsked: () => void }) {
  const { commit, busy } = useCommit();
  const [zerodhaId, setZerodhaId] = useState('');
  const ask = () =>
    void commit(
      { do: 'ask-broker', zerodhaId: zerodhaId.trim() },
      {
        onOk: () => {
          setZerodhaId('');
          onAsked();
        },
      },
    );
  return (
    <Row
      title="Link Zerodha"
      hint="Your Zerodha client ID, the one you log in to Kite with. The desk sets up your connection, then you log in once."
    >
      <div className="ph-no-capture flex flex-wrap gap-2">
        <input
          className={clsx('max-w-40 uppercase', field({ mono: true }))}
          value={zerodhaId}
          onChange={(e) => setZerodhaId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && zerodhaId.trim()) ask();
          }}
          placeholder="AB1234"
          aria-label="Zerodha client ID"
          autoComplete="off"
          maxLength={8}
        />
        <Button tone="primary" busy={busy} disabled={!zerodhaId.trim()} onClick={ask}>
          Link it
        </Button>
      </div>
    </Row>
  );
}

function AskedRow({
  asked,
  onDone,
}: {
  asked: NonNullable<BrokerStatus['asked']>;
  onDone: () => void;
}) {
  const { commit, busy } = useCommit();
  return (
    <Row
      title={
        <span className="flex items-center gap-2">
          Zerodha
          <span className="ph-no-capture font-mono text-xs text-ink-muted">{asked.zerodhaId}</span>
        </span>
      }
      hint={
        <span className="flex gap-2">
          <StatusDot tone={asked.failed ? 'warn' : 'off'} className="mt-1.5" />
          <span>
            {asked.failed
              ? 'The desk couldn’t set up your Zerodha itself. An admin will.'
              : 'Waiting for the desk to set up your Zerodha. You’ll hear when it’s ready to log in.'}
          </span>
        </span>
      }
      control={
        <Button
          size="sm"
          tone="undo"
          busy={busy}
          onClick={() => void commit({ do: 'unlink-broker' }, { onOk: onDone })}
        >
          Withdraw
        </Button>
      }
    />
  );
}

/** For the login whose redirect never lands: rare, so it waits behind a press. */
function TokenRow({ onDone }: { onDone: () => void }) {
  const { commit, busy } = useCommit();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState('');
  return (
    <Row
      title="Finish a login by hand"
      hint="If Zerodha never brought you back, paste the request_token from its address bar."
      control={
        !open && (
          <Button size="sm" onClick={() => setOpen(true)}>
            Paste token
          </Button>
        )
      }
    >
      {open && (
        <div className="flex items-center gap-2">
          <input
            className={clsx('min-w-0 flex-1', field({ mono: true }))}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="request_token"
            aria-label="request_token"
            autoComplete="off"
          />
          <Button
            tone="primary"
            busy={busy}
            disabled={token.trim().length < 8}
            onClick={() =>
              void commit(
                { do: 'broker-token', requestToken: token.trim() },
                {
                  onOk: () => {
                    setToken('');
                    setOpen(false);
                    onDone();
                  },
                },
              )
            }
          >
            Use it
          </Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      )}
    </Row>
  );
}

function UnlinkRow({ onDone }: { onDone: () => void }) {
  const { commit, busy } = useCommit();
  const [sure, setSure] = useState(false);
  return (
    <Row
      title="Unlink Zerodha"
      hint="Wipes your key and token, and pulls every fallback stop and resting entry the desk has at your broker."
      control={
        !sure && (
          <Button size="sm" tone="undo" onClick={() => setSure(true)}>
            Unlink
          </Button>
        )
      }
    >
      {sure && (
        <Confirm
          said="Nothing of the desk's stays standing behind your positions at your broker. It cannot be undone; you would link again from scratch."
          keep="Keep it"
          onKeep={() => setSure(false)}
          act="Unlink"
          onAct={() => void commit({ do: 'unlink-broker' }, { onOk: onDone })}
          busy={busy}
        />
      )}
    </Row>
  );
}

function PlacingSwitch({ placing, onChanged }: { placing: boolean; onChanged: () => void }) {
  const { commit, busy } = useCommit();

  const flip = async (enabled: boolean) => {
    await commit({ do: 'set-placing', enabled });
    // Re-read either way: the switch shows the bot's answer, never the tap.
    onChanged();
  };

  return (
    <Switch on={placing} busy={busy} onChange={(next) => void flip(next)} label="Place my orders" />
  );
}

function YourBroker({
  status,
  look,
  checking,
  onChanged,
}: {
  status: BrokerStatus;
  look: BrokerLook;
  checking: boolean;
  onChanged: () => void;
}) {
  if (!status.linked) {
    return (
      <Rows>
        {status.asked ? (
          <AskedRow asked={status.asked} onDone={onChanged} />
        ) : (
          <AskForm onAsked={onChanged} />
        )}
      </Rows>
    );
  }
  return (
    <Rows>
      <Row
        title={
          <span className="flex items-center gap-2">
            Zerodha
            {status.kiteUserId && (
              <span className="ph-no-capture font-mono text-xs text-ink-muted">
                {status.kiteUserId}
              </span>
            )}
          </span>
        }
        hint={
          <span className="flex gap-2">
            <StatusDot tone={look.tone} className="mt-1.5" />
            <span>{look.line}</span>
          </span>
        }
        control={
          <>
            <RefreshButton
              label="Check with your broker again"
              checking={checking}
              onClick={onChanged}
            />
            {look.login && (
              <BrokerLogin tone={look.login}>
                {look.login === 'primary' ? 'Log in' : 'Log in again'}
              </BrokerLogin>
            )}
          </>
        }
      />
      <Row
        title="Place my orders"
        hint={
          <>
            {status.placing
              ? 'The desk places the entries you confirm, what autopilot takes, and every exit of those, with a fallback stop at your broker behind each.'
              : 'The desk sends nothing, entries or exits. Fallback stops already parked stay.'}
            {status.refusal === 'trading-disabled' && (
              <span className="mt-1.5 block text-warn">
                This deployment can’t write to your broker right now: nothing is placed, sent,
                parked or pulled. Your positions are still recorded here.
              </span>
            )}
          </>
        }
        control={<PlacingSwitch placing={status.placing} onChanged={onChanged} />}
      />
      <TokenRow onDone={onChanged} />
      <UnlinkRow onDone={onChanged} />
    </Rows>
  );
}
